import React, { useMemo } from 'react';
import { ShoppingBag } from 'lucide-react';
import { useConsultancyBundle } from '../../lib/consultancyStore';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';
import { mediaSrc } from '../../lib/mediaUrl';

interface SoldVehiclesProps extends DashboardDetailDateProps {
  onClose: () => void;
}

export function SoldVehicles({ onClose, initialDateRange }: SoldVehiclesProps) {
  const [bundle] = useConsultancyBundle();
  const dr = useDashboardDetailDates(initialDateRange);

  const soldVehicleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of bundle.sales) {
      if (!inRange(s.recordedAt, dr.range)) continue;
      if (s.vehicleId) ids.add(String(s.vehicleId));
    }
    return ids;
  }, [bundle.sales, dr.range]);

  const sold = useMemo(
    () =>
      bundle.vehicles.filter(
        (v) =>
          v.status === 'sold' &&
          (soldVehicleIds.has(v.id) || (v.boughtAt ? inRange(v.boughtAt, dr.range) : false))
      ),
    [bundle.vehicles, dr.range, soldVehicleIds]
  );

  const saleRows = useMemo(
    () =>
      bundle.sales
        .filter((s) => inRange(s.recordedAt, dr.range))
        .map((s, i) => ({
          id: `sale-${i}`,
          customer: String(s.customerName ?? '—'),
          at: s.recordedAt,
          price: Math.max(0, parseFloat(String(s.sellingPrice ?? '')) || 0),
        })),
    [bundle.sales, dr.range]
  );

  const totalSales = useMemo(() => saleRows.reduce((s, r) => s + r.price, 0), [saleRows]);

  const exportRows: ExportRow[] = useMemo(() => {
    const inventoryRows: ExportRow[] = sold.map((v) => ({
      Section: 'Sold inventory',
      Vehicle: `${v.name} ${v.model}`.trim(),
      Year: v.year ?? '—',
      ListPrice: v.price,
      Status: 'Sold',
      Buyer: '—',
      SoldAt: '—',
      SalePrice: '—',
    }));
    const postedRows: ExportRow[] = saleRows.map((r) => ({
      Section: 'Posted sale',
      Vehicle: '—',
      Year: '—',
      ListPrice: '—',
      Status: 'Sold',
      Buyer: r.customer,
      SoldAt: new Date(r.at).toLocaleString(),
      SalePrice: r.price,
    }));
    return [...inventoryRows, ...postedRows];
  }, [sold, saleRows]);

  return (
    <DashboardDetailShell
      onClose={onClose}
      eyebrow="Consultancy desk"
      title="Sold vehicles & sale log"
      subtitle="Sales and sold inventory in the selected period."
      icon={ShoppingBag}
      iconClassName="text-emerald-700"
      exportTitle="Sold Vehicles Report"
      exportFilename="sold-vehicles.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
    >
      <div className="grid grid-cols-2 gap-3 sm:max-w-lg">
        <DashboardMetricCard label="Sold units" value={sold.length} accent />
        <DashboardMetricCard label="Posted sales" value={`₹${totalSales.toLocaleString('en-IN')}`} />
      </div>

      <section className="space-y-3">
        <h2 className="app-section-title">Inventory · sold</h2>
        {sold.length === 0 ? (
          <div className="app-card shadow-sm ring-1 ring-zinc-950/[0.03]">
            <div className="app-card-inner flex flex-col items-center gap-2 py-10 text-center">
              <ShoppingBag className="h-7 w-7 text-zinc-300" aria-hidden />
              <p className="text-sm text-zinc-500">No sold units in this date range.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sold.map((vehicle) => (
              <div key={vehicle.id} className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
                {vehicle.imageUrl ? (
                  <img src={mediaSrc(vehicle.imageUrl)} alt="" className="h-40 w-full object-cover grayscale-[0.15]" />
                ) : (
                  <div className="flex h-40 w-full items-center justify-center bg-zinc-50 text-zinc-300">
                    <ShoppingBag className="h-9 w-9" aria-hidden />
                  </div>
                )}
                <div className="app-card-inner">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="min-w-0 truncate text-base font-semibold text-zinc-900">
                      {vehicle.name} {vehicle.model}
                    </h3>
                    <span className="shrink-0 rounded-full border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">
                      Sold
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-zinc-500">List ref ₹{vehicle.price.toLocaleString('en-IN')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="app-section-title">Posted sales</h2>
        <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="app-table-wrap border-0">
            <table className="app-table min-w-full">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Buyer</th>
                  <th className="text-right">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {saleRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-10 text-center text-sm text-zinc-500">
                      No posted sales in this date range.
                    </td>
                  </tr>
                ) : (
                  saleRows.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-emerald-50/40">
                      <td className="whitespace-nowrap text-xs tabular-nums text-zinc-600">
                        {new Date(r.at).toLocaleString()}
                      </td>
                      <td className="font-medium text-zinc-800">{r.customer}</td>
                      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-zinc-900">
                        ₹{r.price.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </DashboardDetailShell>
  );
}
